/**
 * Ball out of play, goals, corners and goal kicks. (§5.2)
 *
 * Runs LAST in the tick (step.ts stage 9), on the settled post-integration
 * ball position, and only while play is live.
 *
 * The one rule that must be exactly right here is Law 9's "whole ball over the
 * whole line": a ball resting ON the line — or 99% of the way over it — is
 * still IN play. We defer that judgement to `ballFullyCrossedLine` in
 * physics.ts, which tests `ballCoord - radius > lineCoord`, not a naive
 * centre-vs-line compare that would call every touchline ball out ~11cm early
 * (DECISIONS.md D7). Ball HEIGHT is part of the goal test — the ball can cross
 * a line in the air.
 */

import type { StepResult, WorldState } from '../types.js';
import { MatchPhase } from '../types.js';
import { BALL_RADIUS, GOAL_HEIGHT, GOAL_WIDTH, GOAL_AREA_LENGTH, STOPPAGE_GOAL_S } from '../constants.js';
import { ballFullyCrossedLine } from '../physics.js';
import { addStoppage, beginGoalCelebration, beginRestart } from './fsm.js';
import { endShootoutAttempt } from './shootout.js';

/**
 * Team attacking the goal on the +X line. The other line's attacker is its
 * complement. Derived from which way team A is shooting this half.
 */
function attackerOfPositiveXGoal(state: WorldState): number {
  return state.teamAAttacksPositiveX ? 0 : 1;
}

/**
 * Evaluate ball-out for this tick. Sets up the correct restart (throw-in,
 * corner, goal kick, or kickoff after a goal) and emits the rule event.
 */
export function evaluateBallOut(state: WorldState, out: StepResult): void {
  if (state.match.phase !== MatchPhase.InPlay) return;

  const b = state.ball;
  // A shootout has no corners, no goal kicks and no throw-ins: the ball
  // leaving the field just ends the attempt, which `stepShootoutAttempt`
  // detects on its own. The GOAL branch below still runs (a shootout goal is
  // still a ball over the line), so only the not-a-goal restarts are skipped.
  const inShootout = state.match.shootoutActive === 1;
  const r = BALL_RADIUS;
  const halfLength = state.config.pitchLength * 0.5;
  const halfWidth = state.config.pitchWidth * 0.5;

  // ── Goal line (X axis) — check before the touchline so a ball leaving near
  //    the corner is judged a goal / corner / goal-kick, never a throw-in. ────
  let goalSide = 0;
  if (ballFullyCrossedLine(b.px, r, halfLength, true)) goalSide = 1;
  else if (ballFullyCrossedLine(b.px, r, -halfLength, false)) goalSide = -1;

  if (goalSide !== 0) {
    const attackerPosX = attackerOfPositiveXGoal(state);
    // Team attacking THIS goal line scores here; its complement defends it.
    const scoringTeam = goalSide === 1 ? attackerPosX : 1 - attackerPosX;
    const defendingTeam = 1 - scoringTeam;

    // GOAL? Whole ball over the line, between the posts, under the bar. (§5.2)
    const betweenPosts = Math.abs(b.pz) <= GOAL_WIDTH * 0.5;
    const underBar = b.py <= GOAL_HEIGHT && b.py >= 0;
    if (betweenPosts && underBar) {
      // ── INSIDE A SHOOTOUT ─────────────────────────────────────────────────
      // The match score is FINAL — it ended level and stays level in the
      // record ("2-2, 4-3 on penalties"). A shootout goal counts on the
      // shootout's own tally and does NOT kick off; the kick simply ends.
      //
      // Guarded on the kicking side too: a shot that comes back off the
      // keeper and goes in off a defender is still the taker's goal, but a
      // ball that somehow ends up in the OTHER net is not a goal for anyone.
      if (state.match.shootoutActive === 1) {
        const scored = scoringTeam === state.match.restartTeam;
        out.events.push({
          type: 'GOAL',
          tick: state.tick,
          team: scoringTeam,
          player: b.lastTouchedBy,
          x: b.px,
          z: b.pz,
        });
        endShootoutAttempt(state, scored, out);
        return;
      }
      if (scoringTeam === 0) state.match.scoreA++;
      else state.match.scoreB++;
      out.events.push({
        type: 'GOAL',
        tick: state.tick,
        team: scoringTeam,
        player: b.lastTouchedBy,
        x: b.px,
        z: b.pz,
      });
      addStoppage(state, STOPPAGE_GOAL_S);
      // Celebrate first, THEN kick off to the conceding team. This used to call
      // beginKickoff() right here, which meant play resumed KICKOFF_SETUP_S
      // (2.0s) after the ball crossed the line — less of a pause than a
      // throw-in gets, with the GOAL banner (2.8s) still on screen when the
      // next kickoff was already live. It also meant MatchPhase.Goal was never
      // entered, so online clients — whose banners ride the phase feed, not the
      // event feed — had no way to know a goal happened at all.
      beginGoalCelebration(state, defendingTeam);
      return;
    }

    if (inShootout) return; // the attempt ends; stepShootoutAttempt sees it
    // Not a goal: corner if a defender put it out, goal kick if an attacker did.
    const goalLineX = goalSide * halfLength;
    if (b.lastTouchedTeam === defendingTeam) {
      // Corner to the attacking team, on the ball's side of the pitch.
      const cornerZ = b.pz >= 0 ? halfWidth : -halfWidth;
      out.events.push({
        type: 'BALL_OUT_GOAL_LINE',
        tick: state.tick,
        team: scoringTeam,
        player: b.lastTouchedBy,
        x: goalLineX,
        z: cornerZ,
      });
      beginRestart(state, MatchPhase.Corner, scoringTeam, goalLineX, cornerZ);
    } else {
      // Goal kick to the defending team (attacker last touched, or nobody did).
      const gkX = goalSide * (halfLength - GOAL_AREA_LENGTH);
      out.events.push({
        type: 'BALL_OUT_GOAL_LINE',
        tick: state.tick,
        team: defendingTeam,
        player: b.lastTouchedBy,
        x: gkX,
        z: 0,
      });
      beginRestart(state, MatchPhase.GoalKick, defendingTeam, gkX, 0);
    }
    return;
  }

  // ── Touchline (Z axis) → throw-in to the team that did NOT touch it last. ──
  let overTouchline = false;
  if (ballFullyCrossedLine(b.pz, r, halfWidth, true)) overTouchline = true;
  else if (ballFullyCrossedLine(b.pz, r, -halfWidth, false)) overTouchline = true;

  if (overTouchline) {
    if (inShootout) return; // no throw-ins in a shootout — the attempt ends
    const throwTeam = b.lastTouchedTeam >= 0 ? 1 - b.lastTouchedTeam : state.match.possessionTeam;
    const side = b.pz >= 0 ? halfWidth : -halfWidth;
    const x = b.px < -halfLength ? -halfLength : b.px > halfLength ? halfLength : b.px;
    out.events.push({
      type: 'BALL_OUT_SIDE',
      tick: state.tick,
      team: throwTeam,
      player: b.lastTouchedBy,
      x,
      z: side,
    });
    beginRestart(state, MatchPhase.ThrowIn, throwTeam, x, side);
  }
}
